// src/app/core/auth/auth.service.ts
import { Inject, Injectable, InjectionToken } from "@angular/core";
import {
  AccountInfo,
  IPublicClientApplication,
  PublicClientApplication,
  LogLevel,
  Configuration,
  AuthenticationResult
} from "@azure/msal-browser";
import { environment } from "../../core/config/environment";
import { Observable, ReplaySubject } from "rxjs";
import { jwtDecode } from "jwt-decode";
import { LoggerService } from "../../shared/services/logger.service";
import { HashedUserIdService } from "./hashed-userId.service";
import { hash } from "../../shared/utils/hashing-helper";

export const WINDOW = new InjectionToken<Window>("Window", {
  factory: () => window
});

export const OFFLINE_AUTH_TOKEN_KEY = "bqa-offline-auth-token";
const UNAUTHORIZED_ROUTE = "/unauthorized";


// JWT payload interface for typing decoded tokens
interface JwtPayload {
  roles: string[];
  exp?: number;
  iat?: number;
  sub?: string;
  aud?: string;
  iss?: string;
  [key: string]: string | number | boolean | string[] | undefined; // Allow for additional claims
}

@Injectable({
  providedIn: "root"
})
export class AuthService {
  constructor(
    private logger: LoggerService,
    private hashedUserIdService: HashedUserIdService,
    @Inject(WINDOW) private window: Window
  ) { }

  private readonly msal: IPublicClientApplication = new PublicClientApplication({
    auth: {
      clientId: environment.auth.clientId,
      authority: `https://login.microsoftonline.com/${environment.auth.tenantId}/v2.0`,
      redirectUri: environment.auth.redirectUri
    },
    system: { loggerOptions: { logLevel: LogLevel.Warning } }
  } as Configuration);

  private readonly initPromise = this.msal.initialize();
  private readonly token$ = new ReplaySubject<string | null>(1);
  private interactionInProgress = false;

  async init(): Promise<void> {
    this.logger.info("[AuthService] Initializing MSAL instance.");
    await this.initPromise;
  }
  private account: AccountInfo | null = null;
  private userRoles: string[] = [];

  get instance(): IPublicClientApplication {
    return this.msal;
  }

  get currentAccount(): AccountInfo | null {
    return this.account;
  }

  get currentUserRoles(): string[] {
    return this.userRoles;
  }

  // Consumers (interceptors, guards, etc.) use this
  get validToken$(): Observable<string | null> {
    return this.token$.asObservable();
  }

  get isAuthInteractionInProgress(): boolean {
    return this.interactionInProgress;
  }

  private async runInteractiveOperation(operationName: string, operation: () => Promise<void>): Promise<void> {
    if (this.interactionInProgress) {
      this.logger.warn(`[AuthService:${operationName}] Skipping interactive auth call because another interaction is already in progress.`);
      return;
    }

    this.interactionInProgress = true;

    try {
      await operation();
    } finally {
      this.interactionInProgress = false;
    }
  }

  async handleRedirect(): Promise<void> {
    await this.runInteractiveOperation("HandleRedirect", async () => {
      this.logger.info("[AuthService:HandleRedirect] Handling redirect response from MSAL.");

      if (this.isOffline()) {
        this.logger.info("[AuthService:HandleRedirect] Offline — restoring cached token instead of redirecting.");
        const [primary] = this.msal.getAllAccounts();
        await this.restoreOfflineToken(primary);
        return;
      }

      await this.msal.handleRedirectPromise().then(
        async (_: AuthenticationResult | null) => {
          const [primary] = this.msal.getAllAccounts();

          if (primary) {
            this.account = primary;
            this.setHashedUserIdFromAccount();

            try {
              // Acquire token silently, using the environment-configured scope
              this.logger.info("[AuthService:HandleRedirect] Acquiring token silently.");
              const response = this.msal.acquireTokenSilent({
                account: primary,
                scopes: [environment.auth.apiScope],
              });
              const tokenResponse = await response;
              await this.pushToken(tokenResponse.accessToken);
              this.logger.info(`[AuthService:SilentRefresh] Silent token refresh finished at ${new Date().toLocaleString()} ${(await response).fromCache ? "(token acquired from cache)" : "(token acquired from API call)"} `);

            } catch (silentError) {
              this.logger.error("Silent token acquisition failed:", silentError);
              this.logger.info("[AuthService:HandleRedirect] Acquiring token via redirect due to silent acquisition failure.");
              await this.msal.acquireTokenRedirect({
                scopes: [environment.auth.apiScope],
              });
            }
          }
        }
      )
        .catch(async redirectError => {
          this.logger.error("Error handling redirect promise:", redirectError);
          const errorCode = (redirectError as { errorCode?: string })?.errorCode;
          if (errorCode === "state_mismatch") {
            this.logger.warn("[AuthService:HandleRedirect] State mismatch detected. Clearing MSAL cache for a clean retry.");
            await this.msal.clearCache();
            this.window.location.reload();
          }
        });
    });
  }

  async loginRedirect(userPath: string = ""): Promise<void> {
    if (this.isExternallyAuthenticated()) {
      this.logger.info("[AuthService:loginRedirect] External authentication detected. Skipping login redirect.");
      return;
    }

    if (this.isOffline()) {
      this.logger.info("[AuthService:loginRedirect] Offline — restoring cached token instead of redirecting.");
      const [primary] = this.msal.getAllAccounts();
      await this.restoreOfflineToken(primary);
      return;
    }

    await this.runInteractiveOperation("loginRedirect", async () => {
      this.logger.info("[AuthService] Initiating login redirect.");
      try {
        await this.msal.loginRedirect({
          scopes: [],
          redirectStartPage:
            `${environment.auth.redirectUri}/${userPath}`
        });
      } catch (loginError) {
        this.logger.error("Error triggering loginRedirect:", loginError);
        throw loginError;
      }
    });
  }

  logoutRedirect(opts?: { postLogoutRedirectUri?: string }): void {
    if (this.interactionInProgress) {
      this.logger.warn("[AuthService:logoutRedirect] Skipping logout redirect because another interaction is already in progress.");
      return;
    }

    this.interactionInProgress = true;
    this.logger.info("[AuthService] Initiating logout redirect.");
    this.account = null;
    this.userRoles = [];
    this.pushToken(null);

    this.msal.logoutRedirect(opts)
      .finally(() => {
        this.interactionInProgress = false;
      });
  }

  public async trySilentRefresh(): Promise<void> {
    if (this.isExternallyAuthenticated()) {
      this.logger.info("[AuthService:SilentRefresh] External authentication detected. Skipping silent refresh.");
      return;
    }

    if (this.interactionInProgress) {
      this.logger.info("[AuthService:SilentRefresh] Skipping silent refresh because an auth interaction is already in progress.");
      return;
    }

    if (this.isOffline()) {
      this.logger.info("[AuthService:SilentRefresh] Offline — skipping silent refresh and keeping cached token.");
      const [primary] = this.msal.getAllAccounts();
      await this.restoreOfflineToken(primary);
      return;
    }

    if (!this.account) {
      this.logger.warn("[SilentRefresh] User not found. Trying redirect login.");
      await this.runInteractiveOperation("SilentRefresh", async () => {
        await this.msal.acquireTokenRedirect({
          scopes: [environment.auth.apiScope],
        });
      });
      return;
    }
    const [primary] = this.msal.getAllAccounts();
    if (primary) {
      this.account = primary;
      this.setHashedUserIdFromAccount();
      try {
        this.logger.info("[AuthService:SilentRefresh] Acquiring token silently.");
        const response = this.msal.acquireTokenSilent({
          account: primary,
          scopes: [environment.auth.apiScope],
        });
        await this.pushToken((await response).accessToken);
        this.logger.info(`[AuthService:SilentRefresh] Silent token refresh finished at ${new Date().toLocaleString()} ${(await response).fromCache ? "(token acquired from cache)" : "(token acquired from API call)"} `);

      } catch (silentError) {
        this.logger.error("[AuthService:SilentRefresh] Silent token acquisition failed:", silentError);
        if (this.isOffline()) {
          this.logger.info("[AuthService:SilentRefresh] Offline — restoring cached token instead of redirecting.");
          await this.restoreOfflineToken(primary);
          return;
        }
        await this.runInteractiveOperation("SilentRefresh", async () => {
          await this.msal.acquireTokenRedirect({
            scopes: [environment.auth.apiScope],
          });
        });
      }
    }
  }

  public isOffline(): boolean {
    return !this.window.navigator.onLine;
  }

  private async restoreOfflineToken(account: AccountInfo | null | undefined): Promise<void> {
    const expectedValidTenantId = environment.auth.tenantId.toLowerCase();
    const expectedValidClientId = environment.auth.clientId.toLowerCase();
    const cachedToken = this.window.sessionStorage.getItem(OFFLINE_AUTH_TOKEN_KEY);
    const decodedCachedToken = cachedToken && jwtDecode<JwtPayload>(cachedToken);

    const isOfflineCachedSessionValid = account?.idToken
      && account.tenantId.toLowerCase() === expectedValidTenantId
      && decodedCachedToken
      && decodedCachedToken["appid"]?.toString()?.toLowerCase() === expectedValidClientId;

    if (isOfflineCachedSessionValid) {
      this.account = account;
      this.setHashedUserIdFromAccount();
      this.logger.info("[AuthService] Restoring offline cached token.");
      await this.pushToken(cachedToken, { persistToSession: false });
      return;
    }

    this.logger.error("[AuthService] Could not restore valid offline auth. Redirecting to unauthorized.");
    this.account = null;
    await this.pushToken(null);
    if (this.window.location.pathname !== UNAUTHORIZED_ROUTE) {
      this.window.location.assign(UNAUTHORIZED_ROUTE);
    }
  }

  private async pushToken(token: string | null, options: { persistToSession: boolean } = { persistToSession: true }): Promise<void> {
    this.token$.next(token);

    if (token) {
      const shouldPersist = options.persistToSession;
      if (shouldPersist) {
        this.window.sessionStorage.setItem(OFFLINE_AUTH_TOKEN_KEY, token);
      }
      await this.decodeTokenForRolesAndExpiration(token);
    } else {
      this.userRoles = [];
      this.window.sessionStorage.removeItem(OFFLINE_AUTH_TOKEN_KEY);
    }
  }

  public async decodeTokenForRolesAndExpiration(token: string): Promise<void> {
    try {
      // Use jwt-decode library for reliable JWT parsing
      const payload = jwtDecode<JwtPayload>(token);

      // Check for roles claim
      if (payload.roles && Array.isArray(payload.roles) && payload.roles.length > 0) {
        this.userRoles = payload.roles;
      } else {
        this.userRoles = [];
      }

      let currentTokenExpiration = 0;
      if (payload.exp) {
        currentTokenExpiration = payload.exp;
      }

      this.logger.infoWithTrace(`[AuthService] Decoded JWT roles: ${this.userRoles.join(", ")} for userId ${payload["oid"]}`);
      this.logger.info(`[AuthService] Current token expiration: ${(new Date((currentTokenExpiration) * 1000)).toLocaleString()}`);

    } catch (error) {
      this.logger.error("Error decoding JWT:", error);
      this.userRoles = [];
    }
  }

  // Check if user has MarketAdministrator role (full access)
  isAdmin(): boolean {
    if (!this.currentAccount) {
      return false;
    }

    return this.userRoles.includes("MarketAdministrator");
  }

  // Check if user has CustomerCareManager role (CCM view access)
  isCustomerCareManager(): boolean {
    if (!this.currentAccount) {
      return false;
    }

    return this.userRoles.includes("CustomerCareManager");
  }

  // Check if user has ConstructionManager role (CM view access)
  isConstructionManager(): boolean {
    if (!this.currentAccount) {
      return false;
    }

    return this.userRoles.includes("ConstructionManager");
  }

  // Check if user can access construction manager view routes (ConstructionManager or MarketAdministrator only)
  canAccessCMView(): boolean {
    if (!this.currentAccount) {
      return false;
    }

    // If no roles are loaded yet, allow access temporarily (only if authenticated)
    if (this.userRoles.length === 0) {
      return true;
    }

    return this.isConstructionManager() || this.isAdmin();
  }

  //  Check if user can access customer care manager view routes (MarketAdministrator or CustomerCareManager only)
  canAccessCCMView(): boolean {
    if (!this.currentAccount) {
      return false;
    }

    // If no roles are loaded yet, allow access temporarily (only if authenticated)
    if (this.userRoles.length === 0) {
      return true;
    }

    return this.isAdmin() || this.isCustomerCareManager();
  }

  public isExternallyAuthenticated(): boolean {
    const pathname = this.window.location.pathname;
    const isVendorPath = pathname.startsWith("/vendors");
    const isHomeownerPath = pathname.startsWith("/homeowners");
    return isVendorPath || isHomeownerPath;
  }

  private setHashedUserIdFromAccount(): void {
    if (this.account && this.account.localAccountId) {
      hash(this.account.localAccountId.toLowerCase())
        .then(hashedId => {
          this.hashedUserIdService.setHashedUserId(hashedId);
        });
    }
  }
}